import React, { useState } from 'react';
import {
  useAdminTagColors, useUpdateTagColors, useDeleteTagColor, useInventoryTags, useBuyerTags, useSetBuyerTag,
} from '../hooks/useTagColors';
import { adminTagRows } from '../utils/buyerTags';
import { errorMessage } from '../utils/datasetStatus';

const NO_COLORS = {};
const NO_TAGS = [];

// A list that failed to load: the server's message and a Retry
function LoadError({ q, fallback }) {
  if (!q.error) return null;
  return (
    <div className="message message-error tags-load-error" role="alert">
      <span>{errorMessage(q.error, fallback)}</span>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => q.refetch()} disabled={q.isFetching}>
        Retry
      </button>
    </div>
  );
}

// One tag's Buyer box: ticked when it is a saved buyer tag; a click saves at
// once. Each row has its own save, so the box is disabled only while its own
// save runs (and reads back), showing the state being saved; a failed save
// says why next to the box and leaves the saved state ticked as it was.
function BuyerCheckbox({ name, isBuyer, canEdit, onMessage }) {
  const setBuyerTag = useSetBuyerTag();
  const isSaving = setBuyerTag.isPending;
  const checked = isSaving ? setBuyerTag.variables.isBuyer : isBuyer;

  const handleChange = (e) => {
    const next = e.target.checked;
    setBuyerTag.mutate({ tag: name, isBuyer: next }, {
      onSuccess: () => onMessage(next ? `${name} is a buyer tag` : `${name} is no longer a buyer tag`),
    });
  };

  return (
    <>
      <input
        type="checkbox"
        checked={checked}
        disabled={!canEdit || isSaving}
        onChange={handleChange}
        aria-label={`${name} is a buyer`}
      />
      {setBuyerTag.error && (
        <span className="tags-row-error" role="alert">{errorMessage(setBuyerTag.error, 'Failed to save')}</span>
      )}
    </>
  );
}

// One tag's colour actions: Save a picked colour, Remove the saved one. Each
// row has its own saves, so a failure says why next to its own buttons.
// onSaved(tagName): the picked colour is saved (the draft can go).
function ColorActions({ tagName, savedColor, editingColor, onSaved, onMessage }) {
  const updateTagColors = useUpdateTagColors();
  const deleteTagColor = useDeleteTagColor();
  const hasColor = !!savedColor;
  let failure = null;
  if (updateTagColors.error) failure = errorMessage(updateTagColors.error, 'Failed to save the color');
  else if (deleteTagColor.error) failure = errorMessage(deleteTagColor.error, 'Failed to remove the color');

  const handleSave = () => {
    deleteTagColor.reset();
    updateTagColors.mutate({ [tagName]: editingColor }, {
      onSuccess: () => {
        onSaved(tagName);
        onMessage('Tag color saved');
      },
    });
  };
  const handleRemove = () => {
    updateTagColors.reset();
    deleteTagColor.mutate(tagName, { onSuccess: () => onMessage('Tag color removed') });
  };

  return (
    <>
      {editingColor && editingColor !== savedColor && (
        <button
          className="btn btn-small"
          onClick={handleSave}
          disabled={updateTagColors.isPending}
          style={{ marginRight: '0.5rem' }}
        >
          Save
        </button>
      )}
      {hasColor && !editingColor && (
        <button className="btn btn-small btn-danger" onClick={handleRemove} disabled={deleteTagColor.isPending}>
          Remove
        </button>
      )}
      {!hasColor && !editingColor && (
        <span style={{ color: 'var(--color-text-light)', fontSize: '0.75rem' }}>No color set</span>
      )}
      {failure && <span className="tags-row-error" role="alert">{failure}</span>}
    </>
  );
}

// Admin → Tags: a colour per inventory tag, and which tags are buyer names
// (the Buyer box; ticked ones drive Reports → Purchases by tag). The rows are
// the tags on live listings plus the saved buyer tags, so a buyer tag no
// listing carries any more can still be unticked. onMessage(text): the
// page's success banner.
function TagsAdminTab({ onMessage }) {
  const [editingTagColors, setEditingTagColors] = useState({});
  const colorsQ = useAdminTagColors();
  const inventoryQ = useInventoryTags();
  const buyersQ = useBuyerTags();

  if (colorsQ.isLoading || inventoryQ.isLoading || buyersQ.isLoading) return <div className="loading">Loading...</div>;

  const tagColors = colorsQ.data ?? NO_COLORS;
  const hasInventoryTags = Array.isArray(inventoryQ.data);
  const rows = adminTagRows(inventoryQ.data ?? NO_TAGS, buyersQ.data ?? NO_TAGS);
  // With the saved buyer tags never loaded, an unticked box could be a buyer: no edits until they
  // load (a failed refresh keeps the last list, and the boxes stay usable)
  const canEditBuyers = Array.isArray(buyersQ.data);
  const emptyText = inventoryQ.error ? 'The inventory tags did not load' : 'No tags found in inventory';

  const handleColorSaved = (tagName) => setEditingTagColors(prev => {
    const updated = { ...prev };
    delete updated[tagName];
    return updated;
  });

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Manage Tag Colors</h2>
      </div>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: '1rem', fontSize: '0.85rem' }}>
        Assign colors to inventory tags.
        {hasInventoryTags && ` Found ${inventoryQ.data.length} unique tags in your inventory.`}
        {' '}Tick Buyer on the tags that are buyer names: ticked Buyer tags drive Reports → Purchases by tag.
      </p>
      <LoadError q={inventoryQ} fallback="Failed to load the inventory tags" />
      <LoadError q={colorsQ} fallback="Failed to load the tag colors" />
      <LoadError q={buyersQ} fallback="Failed to load the buyer tags" />

      {/* All inventory tags, plus the saved buyer tags */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Tag Name</th>
              <th title="A buyer name: Reports → Purchases by tag groups the listings by these">Buyer</th>
              <th>Color</th>
              <th>Preview</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map(({ name: tagName, isBuyer }) => {
                const savedColor = tagColors[tagName];
                const editingColor = editingTagColors[tagName];
                const displayColor = editingColor || savedColor || '#6b7280';
                const hasColor = !!savedColor;

                return (
                  <tr key={tagName}>
                    <td style={{ fontWeight: '500' }}>{tagName}</td>
                    <td>
                      <BuyerCheckbox name={tagName} isBuyer={isBuyer} canEdit={canEditBuyers} onMessage={onMessage} />
                    </td>
                    <td>
                      <input
                        type="color"
                        value={displayColor}
                        onChange={e => setEditingTagColors(prev => ({ ...prev, [tagName]: e.target.value }))}
                        aria-label={`Colour for ${tagName}`}
                        style={{ width: '40px', height: '28px', padding: '2px', cursor: 'pointer' }}
                      />
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        {displayColor}
                      </span>
                    </td>
                    <td>
                      <span
                        className="tag-chip"
                        style={hasColor || editingColor ? {
                          backgroundColor: displayColor,
                          color: '#fff',
                          borderColor: displayColor
                        } : {}}
                      >
                        {tagName}
                      </span>
                    </td>
                    <td>
                      <ColorActions
                        tagName={tagName}
                        savedColor={savedColor}
                        editingColor={editingColor}
                        onSaved={handleColorSaved}
                        onMessage={onMessage}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TagsAdminTab;
